import { AnyAction } from 'redux';
import { apiActionWrapper, ThunkResult } from 'actions/api';
import { get, post } from 'libs/request';
import { IAddress } from 'types/address';
import { ApiError } from 'types/api-error';
import { shallowEqual } from 'react-redux';
const BASE_ADDRESS_URL = 'https://ecomuser.snssdk.com/address';
const CHECK_LEGAL_STATUS_URL = 'https://ec.snssdk.com/order/checkLegalOrder';

export enum OrderRestriction {
    VERIFIED_OK = 'verified-ok',
    NEED_CODE = 'need-code',
    FORBIDDEN = 'forbidden',
    UNCLEAR = 'unclear'
}
export const SELECT_ADDRESS = 'SELECT_ADDRESS';
export const GET_ADDRESS_LIST = {
    SUCCESS: 'GET_ADDRESS_LIST_SUCCESS',
    FAILURE: 'GET_ADDRESS_LIST_FAILURE',
    REQUEST: 'GET_ADDRESS_LIST_REQUEST'
};

const SAVE_ADDRESS = {
    SUCCESS: 'SAVE_ADDRESS_SUCCESS',
    FAILURE: 'SAVE_ADDRESS_FAILURE',
    REQUEST: 'SAVE_ADDRESS_REQUEST'
};

const DELETE_ADDRESS = {
    SUCCESS: 'DELETE_ADDRESS_SUCCESS',
    FAILURE: 'DELETE_ADDRESS_FAILURE',
    REQUEST: 'DELETE_ADDRESS_REQUEST'
};

const SELECT_ADDRESS_WITH_API = {
    SUCCESS: 'SELECT_ADDRESS_WITH_API_SUCCESS',
    FAILURE: 'SELECT_ADDRESS_WITH_API_FAILURE',
    REQUEST: 'SELECT_ADDRESS_WITH_API_REQUEST'
};

export const GET_DEFAULT_ADDRESS = {
    REQUEST: 'GET_DEFAULT_ADDRESS_REQUEST',
    SUCCESS: 'GET_DEFAULT_ADDRESS_SUCCESS',
    FAILURE: 'GET_DEFAULT_ADDRESS_FAILURE'
};

export const GET_LEGAL_STATUS = {
    REQUEST: 'GET_LEGAL_STATUS_REQUEST',
    SUCCESS: 'GET_LEGAL_STATUS_SUCCESS',
    FAILURE: 'GET_LEGAL_STATUS_FAILURE'
};

const SEND_LEGAL_CODE = {
    REQUEST: 'SEND_LEGAL_CODE_REQUEST',
    SUCCESS: 'SEND_LEGAL_CODE_SUCCESS',
    FAILURE: 'SEND_LEGAL_CODE_FAILURE'
};

const UPDATE_LEGAL_CODE = 'UPDATE_LEGAL_CODE';
const ASK_FOR_CODE = 'ASK_FOR_CODE';
const CLEAR_SELECTED = 'CLEAR_SELECTED';
const IS_SHOW_ADDRESS = 'IS_SHOW_ADDRESS';

export function askForCode(open: boolean) {
    return {
        type: ASK_FOR_CODE,
        payload: open
    };
}

export const getAddressList: () => ThunkResult<void, { address: AddressState }> = () => (
    dispatch,
    getState
) => {
    const addressState = getState().address;
    if (addressState.listLoading || addressState.list !== undefined) return;
    return dispatch(apiActionWrapper(GET_ADDRESS_LIST, get(`${BASE_ADDRESS_URL}/getlist`)));
};

export const saveAddress = (add: IAddress, saveFallBack?: boolean) => {
    const data = { ...add };
    return apiActionWrapper(
        {
            ...SAVE_ADDRESS,
            SUCCESS: (payload) => (dispatch) => {
                dispatch({
                    type: SAVE_ADDRESS.SUCCESS,
                    payload: {
                        ...data,
                        id: payload.id
                    }
                });
            },
            FAILURE: (err) => (dispatch) => {
                // 50000是后端限流，这种错误发生，并且有saveFallBack时候不阻塞主流程
                if (err.code === 50000 && saveFallBack) {
                    dispatch({
                        type: SAVE_ADDRESS.SUCCESS,
                        payload: {
                            ...data
                        }
                    });
                } else {
                    dispatch({
                        type: SAVE_ADDRESS.FAILURE,
                        payload: data
                    });
                }
            }
        },
        post(`${BASE_ADDRESS_URL}/save`, { ...data, add_id: data.id })
    );
};
export const selectAddressWithApi = (id: string) => {
    return apiActionWrapper(
        SELECT_ADDRESS_WITH_API,
        get(`${BASE_ADDRESS_URL}/select?add_id=${id}`)
    );
};
export const deleteAddress = (id: string) => {
    return apiActionWrapper(
        {
            ...DELETE_ADDRESS,
            SUCCESS: () => (dispatch) => {
                dispatch({
                    type: DELETE_ADDRESS.SUCCESS,
                    payload: { id }
                });
            }
        },
        get(`${BASE_ADDRESS_URL}/disable?add_id=${id}`)
    );
};
export const getDefaultAddress = () => {
    return apiActionWrapper<IAddress, { address: AddressState }>(
        {
            ...GET_DEFAULT_ADDRESS,
            SUCCESS: (payload: IAddress): ThunkResult<void, { address: AddressState }> => (
                dispatch
            ) => {
                if (!payload || Array.isArray(payload) || !payload.id) {
                    dispatch({
                        type: GET_DEFAULT_ADDRESS.FAILURE
                    });
                } else {
                    const address = provIdPatch(payload);
                    dispatch({
                        type: GET_DEFAULT_ADDRESS.SUCCESS,
                        payload: address
                    });
                }
            }
        },
        get(`${BASE_ADDRESS_URL}/getaddress?add_id=-1`)
    );
};

export const switchAddressStatus = (payload: boolean) => {
    return {
        type: IS_SHOW_ADDRESS,
        payload: payload
    };
};

export const selectAddress = (payload: IAddress | undefined) => {
    return {
        type: SELECT_ADDRESS,
        payload: payload
    };
};

export function getLegalStatus(productId: string, mobile: string, addressId: string) {
    return apiActionWrapper(
        GET_LEGAL_STATUS,
        get(CHECK_LEGAL_STATUS_URL, {
            params: {
                post_tel: mobile,
                product_id: productId,
                address_id: addressId
            }
        })
    );
}

interface LegalCode {
    mobile: string;
    id: number;
}

export const sendLegalCode = (data: LegalCode) => {
    return apiActionWrapper(
        SEND_LEGAL_CODE,
        post('https://ec.snssdk.com/order/sendcode', {
            tel: data.mobile,
            id: data.id
        })
    );
};

export const updateLegalCode = (code: string) => {
    return {
        type: UPDATE_LEGAL_CODE,
        payload: code
    };
};

export const clearSelectedAddress = () => {
    return {
        type: CLEAR_SELECTED
    };
};

export interface AddressState {
    list?: IAddress[];
    listLoading: boolean;
    defaultLoading: boolean;
    selected?: IAddress;
    legalStatus: OrderRestriction;
    legalCode: string;
    defaultAddressFailure?: ApiError;
    saveLoading: boolean;
    askForCode: boolean;
    cartPostAmount?: number;
    cartLimitGoods?: string[];
    cartPostDetail?: { group_id: string; post_amount: number }[];
    // 是否在地址页
    isShow: boolean;
}
export interface WithAddressState {
    address: AddressState;
}

const initState: AddressState = {
    listLoading: false,
    defaultLoading: true,
    legalStatus: OrderRestriction.VERIFIED_OK,
    legalCode: '',
    saveLoading: false,
    askForCode: false,
    isShow: false
};

const provIdPatch = (address: IAddress): IAddress => {
    return address.prov_id.length === 2
        ? { ...address, prov_id: address.prov_id + '0000' }
        : address;
};

// eslint-disable-next-line complexity
export default (state = initState, action: AnyAction): AddressState => {
    switch (action.type) {
        case GET_ADDRESS_LIST.REQUEST:
            return { ...state, listLoading: true };
        case GET_ADDRESS_LIST.SUCCESS: {
            const list = action.payload.map(provIdPatch);
            return {
                ...state,
                listLoading: false,
                list: list
            };
        }
        case GET_ADDRESS_LIST.FAILURE:
            return { ...state, listLoading: false };
        case SAVE_ADDRESS.REQUEST:
            return { ...state, saveLoading: true };
        case IS_SHOW_ADDRESS:
            return { ...state, isShow: Boolean(action.payload) };
        case SAVE_ADDRESS.FAILURE:
            return { ...state, saveLoading: false };
        case SAVE_ADDRESS.SUCCESS: {
            let list = [...(state.list || [])];
            if (action.payload.is_default) {
                list = list.map((item) => ({ ...item, is_default: 0 }));
            }
            return {
                ...state,
                list: [
                    action.payload,
                    ...list.filter((address) => address.id !== action.payload.id)
                ],
                saveLoading: false
            };
        }
        case DELETE_ADDRESS.SUCCESS:
            return {
                ...state,
                list: (state.list || []).filter((address) => address.id !== action.payload.id)
            };
        case SELECT_ADDRESS:
            return { ...state, selected: action.payload };

        // 风控信息
        case GET_LEGAL_STATUS.REQUEST:
            return { ...state, legalStatus: OrderRestriction.UNCLEAR };
        case GET_LEGAL_STATUS.SUCCESS:
            return { ...state, legalStatus: OrderRestriction.VERIFIED_OK };
        case GET_LEGAL_STATUS.FAILURE: {
            if (!action.payload) return state;
            const code = action.payload.code;
            const legalStatus =
                code === 10005 ? OrderRestriction.FORBIDDEN : OrderRestriction.NEED_CODE;
            return {
                ...state,
                legalCode: '',
                legalStatus
            };
        }
        case UPDATE_LEGAL_CODE:
            return { ...state, askForCode: false, legalCode: action.payload };
        case ASK_FOR_CODE:
            return { ...state, askForCode: action.payload };
        case GET_DEFAULT_ADDRESS.REQUEST:
            return { ...state, defaultLoading: true };
        case CLEAR_SELECTED:
            return { ...state, selected: undefined };
        case GET_DEFAULT_ADDRESS.SUCCESS:
            return {
                ...state,
                defaultLoading: false,
                selected: state.selected || (action.payload.id && action.payload)
            };
        case GET_DEFAULT_ADDRESS.FAILURE:
            return { ...state, defaultLoading: false };
        default:
            return state;
    }
};
